import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { MemberPhotoUploader, type MemberPhotoPatch } from '@/components/common/MemberPhotoUploader'
import { memberProfileService } from '../services/memberProfile.service'
import type { CompanyMemberDocument } from '@/types'

interface FormState {
  bio: string
  instagram: string
  website: string
}

export function MemberProfileForm({ memberId }: { memberId: string }) {
  const [member, setMember] = useState<CompanyMemberDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const [squarePhotoUrl, setSquarePhotoUrl] = useState('')
  const [portraitPhotoUrl, setPortraitPhotoUrl] = useState('')
  const [form, setForm] = useState<FormState>({ bio: '', instagram: '', website: '' })

  useEffect(() => {
    setLoading(true)
    memberProfileService
      .get(memberId)
      .then((doc) => {
        if (!doc) {
          setError(withErrorCode('Your member record could not be loaded.', 'app/validation'))
          return
        }
        setMember(doc)
        setForm({
          bio: doc.bio ?? '',
          instagram: doc.socialLinks?.instagram ?? '',
          website: doc.socialLinks?.website ?? '',
        })
        setSquarePhotoUrl(doc.squarePhotoUrl ?? doc.photoUrl ?? '')
        setPortraitPhotoUrl(doc.portraitPhotoUrl ?? '')
      })
      .catch((err: unknown) => setError(formatUserFacingError(err, 'Failed to load profile')))
      .finally(() => setLoading(false))
  }, [memberId])

  async function handlePhotosUploaded(patch: MemberPhotoPatch) {
    if (patch.squarePhotoUrl) setSquarePhotoUrl(patch.squarePhotoUrl)
    if (patch.portraitPhotoUrl) setPortraitPhotoUrl(patch.portraitPhotoUrl)
    await memberProfileService.update(memberId, patch)
    setSavedAt(new Date())
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await memberProfileService.update(memberId, {
        bio: form.bio,
        socialLinks: { instagram: form.instagram, website: form.website },
      })
      setSavedAt(new Date())
    } catch (err) {
      setError(formatUserFacingError(err, 'Save failed'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSave} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="bio">Bio</Label>
        <Textarea
          id="bio"
          rows={6}
          placeholder="Tell people a bit about yourself…"
          value={form.bio}
          onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
        />
      </div>

      <MemberPhotoUploader
        slug={member?.slug || memberId}
        storageKey={memberId}
        squarePhotoUrl={squarePhotoUrl}
        portraitPhotoUrl={portraitPhotoUrl}
        onUploaded={handlePhotosUploaded}
        helperText="Square photo is used for circular avatars; portrait is shown on your member page."
      />

      <div className="space-y-3">
        <Label className="text-base font-semibold">Social Links</Label>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="instagram">Instagram</Label>
            <Input
              id="instagram"
              placeholder="@username or full URL"
              value={form.instagram}
              onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              placeholder="https://example.com"
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
        {savedAt && (
          <p className="text-sm text-muted-foreground">Saved {savedAt.toLocaleTimeString()}</p>
        )}
      </div>
    </form>
  )
}
